/**
 * Client-side file type resolution for uploads.
 *
 * Browsers populate `File.type` from the OS registry, which is unreliable for
 * text formats: `.md` commonly arrives as "" or "text/x-markdown", and `.txt`
 * occasionally as "". The server never trusts the client MIME (it sniffs
 * content), so this is purely a UX pre-check — it decides which files we
 * attempt to upload and which strategy list / size cap to show.
 */

export type SupportedFormat = {
  mime: string;
  extension: string;
  extensions?: string[];
  label: string;
  maxFileSizeMB?: number;
};

const TEXT_MIME_TYPES = ["text/markdown", "text/plain"];

/**
 * Resolve a supported MIME type for a file, mirroring the server's rules:
 *  - a supported extension decides (the server picks the text flavour from
 *    the extension, and browsers report `.md` as "", "text/plain" or
 *    "text/x-markdown" depending on OS/browser);
 *  - otherwise a supported, non-text browser-reported type is accepted
 *    (binary formats are detected server-side by content, not name);
 *  - text types are never accepted on `File.type` alone — the server only
 *    accepts .md/.markdown/.txt, so a `.log` reported as text/plain would be
 *    rejected after a round-trip.
 * Returns null when the file cannot be matched to a supported format.
 */
export function resolveClientMimeType(
  file: { name: string; type: string },
  supportedMimeTypes: string[],
  formats: SupportedFormat[],
): string | null {
  const dot = file.name.lastIndexOf(".");
  if (dot > 0) {
    const ext = file.name.slice(dot).toLowerCase();
    const match = formats.find(
      (f) => f.extension === ext || (f.extensions ?? []).includes(ext),
    );
    if (match) {
      return match.mime;
    }
  }
  if (
    file.type &&
    supportedMimeTypes.includes(file.type) &&
    !TEXT_MIME_TYPES.includes(file.type)
  ) {
    return file.type;
  }
  return null;
}

/** Max upload size in bytes for a resolved MIME type (falls back to the global cap). */
export function maxFileSizeBytesFor(
  mime: string | null,
  formats: SupportedFormat[],
  globalMaxMB: number,
): number {
  const perType = mime
    ? formats.find((f) => f.mime === mime)?.maxFileSizeMB
    : undefined;
  return (perType ?? globalMaxMB) * 1024 * 1024;
}

/**
 * Human-readable size note, e.g. "Maximum 50 MB each (Markdown, Text up to 5 MB)".
 */
export function describeSizeLimits(
  formats: SupportedFormat[],
  globalMaxMB: number,
): string {
  const lower = formats.filter(
    (f) => f.maxFileSizeMB !== undefined && f.maxFileSizeMB < globalMaxMB,
  );
  if (lower.length === 0) {
    return `Maximum ${globalMaxMB} MB each`;
  }
  const byCap = new Map<number, string[]>();
  for (const f of lower) {
    const labels = byCap.get(f.maxFileSizeMB!) ?? [];
    if (!labels.includes(f.label)) labels.push(f.label);
    byCap.set(f.maxFileSizeMB!, labels);
  }
  const notes = [...byCap.entries()].map(
    ([mb, labels]) => `${labels.join(", ")} up to ${mb} MB`,
  );
  return `Maximum ${globalMaxMB} MB each (${notes.join("; ")})`;
}
